import httpMethod from 'services/update2025/httpMethod';
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";

import { Confirmation } from "components/Confirmation";
import { ParameterContext } from "components/parameter/ParameterContext";
import { ParameterEditor } from "components/parameter/ParameterEditor";

import BlockDetail from "components/block-detail/BlockDetail";
import { Can } from "configs/CASLContext";
import { deleteParameter, findParameters } from "services/ParameterService";
import { actionUpdate, actionView } from "utils/action";
import { create, del, edit, Parameter, X_TOTAL_COUNT } from "utils/constants";
import { handleError } from "utils/handleError";

export const ParameterList = () => {
    const toast = useRef(null);
    const dt = useRef(null);
    const { t, i18n } = useTranslation(["parameter", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const parameterContext = useContext(ParameterContext);
    const { parameter, resetParameter, setParameter, parameters, setParameters, submitted, setSubmitted, setParameterDialog } = parameterContext;

    const [selectedParameter, setSelectedParameter] = useState(null);
    const [deleteParameterDialog, setDeleteParameterDialog] = useState(false);
    const [deleteParametersDialog, setDeleteParametersDialog] = useState(false);

    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [checkLoad, setCheckLoad] = useState(true);

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        filters: {
            code: { value: null, matchMode: "ilike" },
        },
    });

    const calculationUnits = [
        { label: t("date"), value: "DATE" },
        { label: t("month"), value: "MONTH" },
        { label: t("year"), value: "YEAR" },
        { label: t("hour"), value: "HOUR" },
        { label: t("minute"), value: "MINUTE" },
        { label: t("second"), value: "SECOND" },
        { label: t("times"), value: "TIMES" },
    ];

    useEffect(() => {
        if (checkLoad) {
            searchParams();
        }
    }, [lazyParams]);

    useEffect(() => {
        if (submitted) {
            setLazyParams({
                ...lazyParams,
                first: 0,
                page: 0,
            });
            searchParams();
            setSubmitted(false);
        }
    }, [submitted]);

    const searchParams = () => {
        setLoading(true);
        setSelectedParameter(null);
        setParameter({});
        findParameters(headers, lazyParams)
            .then((res) => {
                setTotalRecords(res.headers[X_TOTAL_COUNT] ? parseInt(res.headers[X_TOTAL_COUNT]) : 0);
                const dataRes = res.data;
                dataRes?.forEach((e, i) => {
                    e.stt = lazyParams.rows * lazyParams.page + 1 + i;
                });
                setParameters(dataRes);
            })
            // .catch((error) => handleError(toast, t, error))
            .finally(() => setLoading(false));
    };

    const openNewDialog = () => {
        resetParameter();
        setSubmitted(false);
        setParameterDialog(true);
        setCheckLoad(false);
    };

    const openEditDialog = () => {
        setParameter({ ...selectedParameter, action: actionUpdate });
        setSubmitted(false);
        setParameterDialog(true);
        setCheckLoad(false);
    };

    const openViewDialog = (param) => {
        setParameter({ ...param, action: actionView });
        setSubmitted(false);
        setParameterDialog(true);
    };

    const hideDeleteParametersDialog = () => {
        setDeleteParametersDialog(false);
    };

    const confirmDeleteParameter = (parameter) => {
        setParameter(parameter);
        setDeleteParameterDialog(true);
    };

    const hideDeleteParameterDialog = () => {
        setDeleteParameterDialog(false);
    };

    const confirmDeleteSelected = () => {
        setDeleteParameterDialog(true);
    };

    const deleteParameterAction = () => {
        deleteParameter(headers, selectedParameter.id)
            .then(() => {
                searchParams();
                setDeleteParameterDialog(false);
                resetParameter();
                toast.current.show({
                    severity: "success",
                    summary: t("success", { ns: "common" }),
                    detail: t("inform.deleted", { ns: "common", object: t("parameter") }),
                    life: 3000,
                });
            })
            .catch((error) => handleError(toast, t, error));
    };

    const deleteSelectedParametersAction = () => {
        deleteParameter(headers, selectedParameter.id)
            .then(() => {
                searchParams();
                setDeleteParametersDialog(false);
                setSelectedParameter(null);
                toast.current.show({
                    severity: "success",
                    summary: t("success", { ns: "common" }),
                    detail: t("inform.deleted", { ns: "common", object: t("parameters") }),
                    life: 3000,
                });
            })
            .catch((error) => handleError(toast, t, error));
    };

    const actionTemplate = () => {
        return (
            <div className="grid">
                <div className="col-12 tex-center mt-2" style={{ display: "flex", justifyContent: "center" }}>
                    {/* tạo mới */}
                    <Button label={t("new", { ns: "common" })} icon="pi pi-plus" className="p-button mr-2" onClick={openNewDialog} />
                    {/* sửa */}
                    <Button disabled={!selectedParameter} label={t("edit", { ns: "common" })} icon="pi pi-pencil" className="p-button mr-2" onClick={openEditDialog} />
                    {/* xóa */}
                    <Button disabled={!selectedParameter} label={t("delete", { ns: "common" })} icon="pi pi-trash" className="p-button-danger p-button mr-2" onClick={confirmDeleteSelected} />
                </div>
            </div>
        );
    };

    const textFilterTemplate = (options) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <InputText value={options.value} onChange={(e) => options.filterApplyCallback(e.target?.value || "")} />
            </div>
        );
    };

    const calculationUnitFilterTemplate = (options) => {
        return <Dropdown value={options.value} options={calculationUnits} onChange={(e) => options.filterApplyCallback(e.value)} />;
    };

    const calculationUnitBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex align-items-center justify-content-center">
                    <p>{t(String(rowData.calculationUnit).toLowerCase())}</p>
                </div>
            </>
        );
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-0">
                    <Can I={create} a={Parameter}>
                        <Button label={t("new", { ns: "common" })} icon="pi pi-plus" className="p-button mr-2" onClick={openNewDialog} />
                    </Can>
                    {selectedParameter?.length > 0 && (
                        <Can I={del} a={Parameter}>
                            <Button label={t("delete", { ns: "common" })} icon="pi pi-trash" className="p-button-danger" onClick={confirmDeleteSelected} />
                        </Can>
                    )}
                </div>
            </React.Fragment>
        );
    };

    const checkboxBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex align-items-center justify-content-center">
                    <Checkbox checked={rowData.enabled} readOnly />
                </div>
            </>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center justify-content-center actions">
                <Can I={edit} a={Parameter}>
                    <Button icon="pi pi-eye" className="p-button-rounded p-button mr-2" tooltip={t("view", { ns: "common" })} onClick={() => openViewDialog(rowData)} />
                </Can>
            </div>
        );
    };

    const onPage = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onSort = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onFilter = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        _lazyParams["first"] = 0;
        setLazyParams(_lazyParams);
    };

    const paginatorTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
        RowsPerPageDropdown: (options) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 20, value: 20 },
                { label: 30, value: 30 },
                { label: 50, value: 50 },
            ];

            return (
                <React.Fragment>
                    <div style={{ width: "140px", display: "inline-block" }}>
                        <span className="mx-1">{t("show", { ns: "common" })}: </span>
                        <Dropdown value={options.value} options={dropdownOptions} onChange={options.onChange} />
                    </div>
                </React.Fragment>
            );
        },
        CurrentPageReport: (options) => {
            return <span style={{ width: "calc(100% - 490px)", textAlign: "center", display: "inline-block" }}>{t("total", { ns: "common", total: options.totalRecords })}</span>;
        },
    };

    const searchOption = (
        <div className="grid">
            {/* Mã Tham số */}
            <div className="col-6">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "130px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="code"
                    >
                        {t("parameter.code")}
                    </label>
                    <div className="col">
                        <InputText
                            style={{
                                width: "100%",
                            }}
                            id="code"
                            name="code"
                            onChange={(e) => {
                                setSubmitted(false);
                                setCheckLoad(false);
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        code: {
                                            ...lazyParams.filters.code,
                                            value: e.target.value,
                                        },
                                    },
                                });
                            }}
                        ></InputText>
                    </div>
                </div>
            </div>

            <div className="col-12 md:col-6 text-right">
                <div className="grid custom-form">
                    <div className="col-12">
                        <label
                            style={{
                                // width: "130px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 0px",
                            }}
                        >
                            {" "}
                        </label>
                    </div>
                    <div className="col-12" style={{ margin: "5px 0px 0px 0px" }}>
                        <Button
                            label={t("search", { ns: "common" })}
                            icon="pi pi-search"
                            onClick={(e) => {
                                setCheckLoad(true);
                                setLazyParams({
                                    ...lazyParams,
                                    first: 0,
                                    page: 0,
                                });
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="grid crud">
            <div className="col-12">
                <div className="card">
                    <div className="grid">
                        <div className="col-12">
                            <h4 className="text-center font-bold">{t("parameter.title")?.toUpperCase()}</h4>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("search", { ns: "common" })}>{searchOption}</BlockDetail>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("parameter.list")}>
                                <Toast ref={toast} />
                                <DataTable
                                    ref={dt}
                                    value={parameters}
                                    selectionMode="single"
                                    selection={selectedParameter}
                                    onSelectionChange={(e) => setSelectedParameter(e.value)}
                                    dataKey="id"
                                    className="datatable-responsive p-datatable-sm"
                                    showGridlines
                                    paginator
                                    lazy
                                    rows={lazyParams.rows}
                                    first={lazyParams.first}
                                    totalRecords={totalRecords}
                                    onPage={onPage}
                                    onSort={onSort}
                                    sortField={lazyParams.sortField}
                                    sortOrder={lazyParams.sortOrder}
                                    onFilter={onFilter}
                                    filters={lazyParams.filters}
                                    loading={loading}
                                    paginatorClassName="justify-content-start"
                                    paginatorTemplate={paginatorTemplate}
                                    emptyMessage="Không có dữ liệu"
                                    scrollable
                                >
                                    <Column field="stt" header={t("stt", { ns: "common" })} style={{ maxWidth: "50px" }} />
                                    <Column field="code" header={t("code")} sortable filter showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="name" header={t("name")} sortable filter showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="value" header={t("value")} sortable filter showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column
                                        field="calculationUnit"
                                        header={t("calculationUnit")}
                                        sortable
                                        filter
                                        showFilterMatchModes={false}
                                        filterElement={calculationUnitFilterTemplate}
                                        body={calculationUnitBodyTemplate}
                                        style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }}
                                    />
                                    <Column field="enabled" header={t("enabled")} dataType="boolean" sortable body={checkboxBodyTemplate} headerStyle={{ width: "10rem" }} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="modifiedDate" header={t("modifiedDate", { ns: "common" })} />
                                    <Column field="modifiedBy.username" header={t("modifiedBy", { ns: "common" })} />
                                    <Column body={actionBodyTemplate} style={{ maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                </DataTable>
                            </BlockDetail>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12">{actionTemplate()}</div>
                    </div>

                    <Confirmation
                        visible={deleteParameterDialog}
                        hideAction={hideDeleteParameterDialog}
                        processAction={deleteParameterAction}
                        confirmMessage={
                            selectedParameter && (
                                <span>
                                    {t("confirm.delete", { ns: "common", object: t("parameter") })} <b>{selectedParameter.name}</b>?
                                </span>
                            )
                        }
                    />

                    <Confirmation
                        visible={deleteParametersDialog}
                        hideAction={hideDeleteParametersDialog}
                        processAction={deleteSelectedParametersAction}
                        confirmMessage={
                            selectedParameter && (
                                <span>
                                    {t("confirm.delete.selected", { ns: "common" })} <b>{selectedParameter.name}</b>?
                                </span>
                            )
                        }
                    />
                    <ParameterEditor />
                </div>
            </div>
        </div>
    );
};
